import * as THREE from 'three'
import gsap from 'gsap'

const parameters = {
    materialColor: '#ffeded'
}


/**
 * Base
 */
// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

const light = new THREE.DirectionalLight('#ffffff', 3)
const ambientLight = new THREE.AmbientLight('#ffffff', 0.06)
light.position.set(0, 1, 1)



const loaderOverlay = document.querySelector('.loader-overlay')
const loaderPercent = document.querySelector('.loader-percent')

const loadingManager = new THREE.LoadingManager(
    // onLoad
    () => {
        loaderPercent.textContent = '100%'
        setTimeout(() => {
            document.body.classList.add('loaded')
        }, 300)
    },

    // onProgress
    (itemUrl, itemsLoaded, itemsTotal) => {
        const progress = Math.round((itemsLoaded / itemsTotal) * 100)
        loaderPercent.textContent = progress + '%'
    }
)

const textureLoader = new THREE.TextureLoader(loadingManager)


const oneColorTexture = textureLoader.load('./textures/1/wood_table_001_diff_2k.png')
const oneARMTexture = textureLoader.load('./textures/1/wood_table_001_arm_2k.png')
const oneNormalTexture = textureLoader.load('./textures/1/wood_table_001_nor_gl_2k.png')
const oneDisplacementTexture = textureLoader.load('./textures/1/wood_table_001_disp_2k.png')

oneColorTexture.colorSpace = THREE.SRGBColorSpace


const twoColorTexture = textureLoader.load('./textures/2/black_painted_planks_diff_2k.png')
const twoARMTexture = textureLoader.load('./textures/2/black_painted_planks_arm_2k.png')
const twoNormalTexture = textureLoader.load('./textures/2/black_painted_planks_nor_gl_2k.png')
const twoDisplacementTexture = textureLoader.load('./textures/2/black_painted_planks_disp_2k.png')

twoColorTexture.colorSpace = THREE.SRGBColorSpace


const threeColorTexture = textureLoader.load('./textures/3/bark_willow_diff_2k.png')
const threeARMTexture = textureLoader.load('./textures/3/bark_willow_arm_2k.png')
const threeNormalTexture = textureLoader.load('./textures/3/bark_willow_nor_gl_2k.png')
const threeDisplacementTexture = textureLoader.load('./textures/3/bark_willow_disp_2k.png')

threeColorTexture.colorSpace = THREE.SRGBColorSpace



const objectsDistance = 4

const mesh1 = new THREE.Mesh(
    new THREE.TorusGeometry(1, 0.4, 256,256),
    new THREE.MeshStandardMaterial({
        map: oneColorTexture,
        aoMap: oneARMTexture,
        roughnessMap: oneARMTexture,
        metalnessMap: oneARMTexture,
        normalMap: oneNormalTexture,
        displacementMap: oneDisplacementTexture,
        displacementScale: 0.05,
        displacementBias: -0.01
    })
)

const mesh2 = new THREE.Mesh(
    new THREE.SphereGeometry(1, 256, 256),
    new THREE.MeshStandardMaterial({
        map: twoColorTexture,
        aoMap: twoARMTexture,
        roughnessMap: twoARMTexture,
        metalnessMap: twoARMTexture,
        normalMap: twoNormalTexture,
        displacementMap: twoDisplacementTexture,
        displacementScale: 0.01,
        displacementBias: -0.01,
    })
)

const mesh3 = new THREE.Mesh(
    new THREE.BoxGeometry(1, 1, 1, 256, 256, 256),
    new THREE.MeshStandardMaterial({
        map: threeColorTexture,
        aoMap: threeARMTexture,
        roughnessMap: threeARMTexture,
        metalnessMap: threeARMTexture,
        normalMap: threeNormalTexture,
        displacementMap: threeDisplacementTexture,
        displacementScale: 0.02,
        displacementBias: -0.01
    })
)

mesh1.position.y = -objectsDistance  * 0
mesh2.position.y = -objectsDistance  * 1
mesh3.position.y = -objectsDistance  * 2

mesh1.position.x = 1
mesh2.position.x = -1
mesh3.position.x = 1


scene.add(mesh1,mesh2,mesh3, light, ambientLight)

const sectionMeshes = [mesh1, mesh2, mesh3]

const particlesCount = 1000
const positions = new Float32Array(particlesCount * 3)

for (let i = 0; i < particlesCount; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 10
    positions[i * 3 + 1] = objectsDistance * 0.5 - Math.random() * objectsDistance * 3
    positions[i * 3 + 2] = (Math.random() - 0.5) * 10
}

const particlesGeometry = new THREE.BufferGeometry().setAttribute('position', new THREE.BufferAttribute(positions, 3))
const particlesMaterial = new THREE.PointsMaterial({
    color: parameters.materialColor, size: 0.01, sizeAttenuation: true
})

const particles = new THREE.Points(particlesGeometry, particlesMaterial)
scene.add(particles)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Camera
 */
const cameraGroup = new THREE.Group()
scene.add(cameraGroup)
// Base camera
const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100)
camera.position.z = 6
cameraGroup.add(camera)

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    alpha: true
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))


let scrollY = window.scrollY
let currentSections = 0

window.addEventListener('scroll', () => {
    scrollY = window.scrollY

    const newSections = Math.round(scrollY / sizes.height)

    if (newSections !== currentSections) {
        currentSections = newSections
        gsap.to(
            sectionMeshes[currentSections].rotation,
            {
                duration: 3,
                ease: 'power2.inOut',
                x: '+=6',
                y: '+=3',
                z: '+=1.5',
            }
        )
    }
})

const cursors = {
    x: 0,
    y: 0
}

window.addEventListener('mousemove', ({ clientX, clientY }) => {
    cursors.x = clientX/sizes.width - 0.5
    cursors.y = clientY/sizes.height - 0.5
    })
/**
 * Animate
 */
const clock = new THREE.Clock()
let previousTime = 0

const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()
    const deltaTime = elapsedTime - previousTime
    previousTime = elapsedTime

    camera.position.y = - scrollY / sizes.height * objectsDistance

    const parallaxX = cursors.x * 0.5
    const parallaxY = - cursors.y * 0.5

    cameraGroup.position.x += (parallaxX - cameraGroup.position.x) * 3 * deltaTime
    cameraGroup.position.y += (parallaxY - cameraGroup.position.y) * 3 * deltaTime

    for (const mesh of sectionMeshes){
        mesh.rotation.x += deltaTime * 0.5
        mesh.rotation.y += deltaTime * 0.5
    }

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()